import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, InputAdornment, CircularProgress,
  Avatar, Alert, Tooltip
} from '@mui/material';
import { Add, CalendarMonth, Close, AccessTime } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const statusConfig: Record<string, { color: string; bg: string; border: string }> = {
  scheduled:    { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  confirmed:    { color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
  'in-progress':{ color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  completed:    { color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  cancelled:    { color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  'no-show':    { color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0' },
};

const apptTypes = ['consultation', 'follow-up', 'emergency', 'surgery', 'diagnostic'];
const timeSlots = Array.from({ length: 20 }, (_, i) => {
  const h = Math.floor(i / 2) + 8;
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});

const Appointments = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [open, setOpen] = useState(false);
  const [patients, setPatients] = useState<any[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    patient: '', doctor: '', appointmentDate: dayjs().format('YYYY-MM-DD'),
    timeSlot: '09:00', type: 'consultation', symptoms: '', fee: 0, notes: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/appointments', { params: { date, limit: 100 } });
      setAppointments(res.data.data || []);
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (open) {
      api.get('/patients', { params: { limit: 200, search: patientSearch } })
        .then(r => setPatients(r.data.data || []));
    }
  }, [open, patientSearch]);

  const handleSubmit = async () => {
    if (!form.patient) { setError('Please select a patient'); return; }
    setError(''); setSubmitting(true);
    try {
      await api.post('/appointments', form);
      toast.success('Appointment booked successfully');
      setOpen(false);
      setForm({ patient: '', doctor: '', appointmentDate: dayjs().format('YYYY-MM-DD'), timeSlot: '09:00', type: 'consultation', symptoms: '', fee: 0, notes: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to book appointment');
    } finally { setSubmitting(false); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await api.put(`/appointments/${id}`, { status });
      toast.success('Status updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const dateButtons = Array.from({ length: 7 }, (_, i) => dayjs().add(i - 1, 'day').format('YYYY-MM-DD'));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Appointments</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>{appointments.length} appointments on {dayjs(date).format('DD MMM YYYY')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setOpen(true); setError(''); }}
          sx={{ borderRadius: 2, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
          Book Appointment
        </Button>
      </Box>

      {/* Date Selector */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <AccessTime sx={{ color: '#94a3b8', mr: 1 }} />
          {dateButtons.map(d => {
            const isToday = d === dayjs().format('YYYY-MM-DD');
            const isSelected = d === date;
            return (
              <Button key={d} onClick={() => setDate(d)} size="small" variant={isSelected ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: isSelected ? 700 : 400, minWidth: 80,
                  bgcolor: isSelected ? '#1e40af' : 'transparent',
                  borderColor: isSelected ? '#1e40af' : '#e2e8f0',
                  color: isSelected ? 'white' : isToday ? '#1e40af' : '#64748b',
                  '&:hover': { bgcolor: isSelected ? '#1d4ed8' : '#f8fafc', borderColor: '#1e40af' }
                }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: 10 }}>{isToday ? 'Today' : dayjs(d).format('ddd')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 14 }}>{dayjs(d).format('D')}</Typography>
                </Box>
              </Button>
            );
          })}
          <TextField type="date" size="small" value={date} onChange={e => setDate(e.target.value)}
            sx={{ ml: 'auto', '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </Box>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Patient', 'Time', 'Type', 'Doctor', 'Symptoms', 'Fee', 'Status', 'Update Status'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', py: 1.5, fontSize: 13, borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map(a => {
                  const sc = statusConfig[a.status] || statusConfig.scheduled;
                  return (
                    <TableRow key={a._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#dbeafe', color: '#1d4ed8', fontSize: 13, fontWeight: 700 }}>
                            {a.patient?.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{a.patient?.name}</Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{a.patient?.patientId}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{a.timeSlot}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={a.type} size="small" variant="outlined" sx={{ textTransform: 'capitalize', fontSize: 11, borderColor: '#e2e8f0' }} />
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: '#374151' }}>{a.doctor?.name || '—'}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.symptoms || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>₹{a.fee || 0}</Typography></TableCell>
                      <TableCell>
                        <Chip label={a.status} size="small"
                          sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize', border: `1px solid ${sc.border}` }} />
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" value={a.status} onChange={e => updateStatus(a._id, e.target.value)}
                          sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}>
                          {Object.keys(statusConfig).map(s => (
                            <MenuItem key={s} value={s} sx={{ fontSize: 13, textTransform: 'capitalize' }}>{s}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {appointments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CalendarMonth sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                      <Typography sx={{ color: '#94a3b8' }}>No appointments on this date</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Book Appointment Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#0f172a' }}>
          Book Appointment
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Search Patient</Typography>
              <TextField fullWidth size="small" placeholder="Type to search patient..."
                value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField select fullWidth size="small" label="Select Patient *" value={form.patient} onChange={e => set('patient', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {patients.map(p => <MenuItem key={p._id} value={p._id}>{p.name} — {p.patientId} {p.phone ? `(${p.phone})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Date *</Typography>
              <TextField fullWidth size="small" type="date" value={form.appointmentDate}
                onChange={e => set('appointmentDate', e.target.value)} InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Time Slot *</Typography>
              <TextField select fullWidth size="small" value={form.timeSlot} onChange={e => set('timeSlot', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {timeSlots.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Type</Typography>
              <TextField select fullWidth size="small" value={form.type} onChange={e => set('type', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {apptTypes.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Consultation Fee (₹)</Typography>
              <TextField fullWidth size="small" type="number" value={form.fee} onChange={e => set('fee', Number(e.target.value))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Symptoms / Reason</Typography>
              <TextField fullWidth size="small" multiline rows={2} value={form.symptoms} onChange={e => set('symptoms', e.target.value)} placeholder="Describe symptoms or reason for visit"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Notes</Typography>
              <TextField fullWidth size="small" multiline rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional notes"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Book Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointments;
